import { useCallback, useEffect, useState } from 'react';
import { isIOS } from 'react-device-detect';
import * as Sentry from '@sentry/react';
import isStandalone from 'pwa/helpers/isStandalone';
import {
  PushStatus,
  getCurrentSubscription,
  getPushStatus,
  isPushSupported,
  subscribeToPush,
  toPayload,
} from 'pwa/helpers/pushNotifications';
import { usePosthogCapture } from 'pwa/posthogCapture/_index';
import {
  fetchVapidPublicKey,
  useRegisterPushSubscription,
  useUnregisterPushSubscription,
} from 'shared/hooks/api/pushSubscriptions';

interface PushNotifications {
  status: PushStatus | null;
  isBusy: boolean;
  hasFailed: boolean;
  enable: () => Promise<void>;
  disable: () => Promise<void>;
}

function currentPermission(): NotificationPermission {
  return 'Notification' in window ? Notification.permission : 'default';
}

/**
 * Push on this device: its status, and switching it on or off. Enabling asks
 * for permission, which iOS only allows from a tap, then subscribes the
 * browser and registers the device with the API.
 */
export default function usePushNotifications(): PushNotifications {
  const [status, setStatus] = useState<PushStatus | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);
  const { mutateAsync: registerDevice } = useRegisterPushSubscription();
  const { mutateAsync: unregisterDevice } = useUnregisterPushSubscription();
  const capture = usePosthogCapture();

  const refreshStatus = useCallback(async () => {
    const subscription = await getCurrentSubscription();

    setStatus(
      getPushStatus({
        isSupported: isPushSupported(),
        isIos: isIOS,
        isInStandaloneMode: isStandalone(),
        permission: currentPermission(),
        isSubscribed: subscription !== null,
      })
    );
  }, []);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  const enable = useCallback(async () => {
    setIsBusy(true);
    setHasFailed(false);

    try {
      const permission = await Notification.requestPermission();

      if (permission !== 'granted') {
        capture.pushBlocked();
        await refreshStatus();
        return;
      }

      const subscription = await subscribeToPush(await fetchVapidPublicKey());
      await registerDevice(toPayload(subscription));
      capture.pushEnabled();
    } catch (error) {
      setHasFailed(true);
      Sentry.captureException(error, { tags: { feature: 'push' } });
    } finally {
      await refreshStatus();
      setIsBusy(false);
    }
  }, [capture, refreshStatus, registerDevice]);

  const disable = useCallback(async () => {
    setIsBusy(true);
    setHasFailed(false);

    try {
      const subscription = await getCurrentSubscription();

      if (subscription) {
        await unregisterDevice(subscription.endpoint);
        await subscription.unsubscribe();
      }

      capture.pushDisabled();
    } catch (error) {
      setHasFailed(true);
      Sentry.captureException(error, { tags: { feature: 'push' } });
    } finally {
      await refreshStatus();
      setIsBusy(false);
    }
  }, [capture, refreshStatus, unregisterDevice]);

  return { status, isBusy, hasFailed, enable, disable };
}
